/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { stripHTML } from '@carbon/ibmdotcom-utilities/es/utilities/stripHTML/index.js';
import { render } from 'lit/html.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import styles from './global-banner.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { stablePrefix: c4dPrefix } = settings;

/**
 * The heading content in global banner
 *
 * @element c4d-global-banner-heading
 */
@customElement(`${c4dPrefix}-global-banner-heading`)
class C4DGlobalBannerHeading extends StableSelectorMixin(LitElement) {
  /**
   * The shadow slot this logo UI should be in.
   */
  @property({ reflect: true })
  slot = 'heading';

  @state()
  content = '';

  connectedCallback() {
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'heading');
    }
    if (!this.hasAttribute('aria-level')) {
      this.setAttribute('aria-level', '2');
    }
    super.connectedCallback();
  }

  firstUpdated() {
    this.content = stripHTML(this.innerHTML);
    render(html` ${this.content} `, this);
  }

  render() {
    return html` ${this.content} `;
  }

  static get stableSelector() {
    return `${c4dPrefix}-global-banner-heading`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DGlobalBannerHeading;
